import { Button, DatePicker, Form, Input, Select } from 'antd';

import { ApplicantUserPrimaryEducationalInformation, ApplicantUserProfessionalInput, Entity, StaffApplicantProfileProfessionEditContainerApplicantUserProfessionalFieldsFragment } from '../../../../generated';
import { MinusCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FormatDate } from '../../../../constants';
import { Country } from '../../../../types/country';
import { Rule } from 'antd/es/form';
import { EditProfile } from './edit-profile';
import { HealthProfession } from '../../../../types/healthProfession';
import { APOLLO_DATE_FORMAT, DATE_FORMAT } from '../../../../constants';
import StateSelect from '../../../shared/state-select';
import { HealthProfessionDropdownContainer } from '../../../shared/health-profession-dropdown/health-profession-dropdown-container';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const EDIT_TITLE = 'Edit Professional Details';
interface ApplicantProfileProfessionEditProps {
  professionalInformation: StaffApplicantProfileProfessionEditContainerApplicantUserProfessionalFieldsFragment;
  countryData: Country[];
  entities: Entity[];
  onCancel: () => void;
  onSave: (applicant: ApplicantUserProfessionalInput) => Promise<void>;
  healthProfessions: HealthProfession[];
}

export const ApplicantProfileProfessionEdit: React.FC<ApplicantProfileProfessionEditProps> = ({ professionalInformation, countryData, onCancel, onSave, entities }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [formError] = React.useState<string>('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [zipCodeRules, setZipCodeRules] = useState<Rule[][]>(professionalInformation?.primaryEducationalInformation?.map(() => [{ required: false, message: '' }]) ?? []);
  const [showOtherSection, setShowOtherSection] = useState<boolean[]>(
    professionalInformation.primaryEducationalInformation?.map((educationDetail) => educationDetail?.issuingInstitution === null && educationDetail?.otherIssuingInstitution !== null ) ?? []
  );
  const initialValues = {
    healthProfession: professionalInformation?.healthProfession,
    citizenshipAtTimeEnteredMedicalInstitution: professionalInformation?.citizenshipAtTimeEnteredMedicalInstitution,
    primaryEducationalInformation: professionalInformation?.primaryEducationalInformation,
    issuingInstitutionInfo: professionalInformation?.primaryEducationalInformation?.map((edInfo) => edInfo?.issuingInstitution) || []
  };

  useEffect(() => {
    const initialCountries =
      form
        .getFieldsValue()
        .primaryEducationalInformation?.map((educationDetail: ApplicantUserPrimaryEducationalInformation) => educationDetail?.otherIssuingInstitution?.country || '') || [];

    const initialZipCodeRules = initialCountries.map((country: string) => {
      return country === 'US' || country === 'CA' ? [{ required: true, message: 'Please enter Zip/Postal Code' }] : [{ required: false, message: '' }];
    });

    setSelectedCountries(initialCountries);
    setZipCodeRules(initialZipCodeRules);
  }, [form]);

  useEffect(() => {
    professionalInformation?.primaryEducationalInformation?.forEach((educationDetail: any, index: any) => {
      if (educationDetail?.issuingInstitution?.id === undefined && educationDetail?.otherIssuingInstitution !== null) {
        form.setFieldValue(['primaryEducationalInformation', index, 'issuingInstitution', 'id'], 'Other');
      }
    });
  }, []);

  const handleEntityChange = (value: string, index: number) => {
    setSelectedCountries((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleCountryChange = (value: string, index: number) => {
    setSelectedCountries((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    // Reset state / province when country changes
    form.setFieldsValue({
      primaryEducationalInformation: form.getFieldsValue().primaryEducationalInformation.map((educationDetail: ApplicantUserPrimaryEducationalInformation, i: number) => {
        if (i === index) {
          return {
            ...educationDetail,
            otherIssuingInstitution: {
              ...educationDetail.otherIssuingInstitution,
              stateOrProvince: ''
            }
          };
        }
        return educationDetail;
      })
    });

    // Update zip code rules for the specific field
    setZipCodeRules((prev) => {
      const updatedRules = [...prev];
      if (value === 'US' || value === 'CA') {
        updatedRules[index] = [{ required: true, message: 'Please enter Zip/Postal Code' }];
      } else {
        updatedRules[index] = [{ required: false, message: '' }];
      }
      return updatedRules;
    });
  };

  const [educationDetails, setEducationDetails] = React.useState<ApplicantUserPrimaryEducationalInformation[]>(
    professionalInformation?.primaryEducationalInformation as ApplicantUserPrimaryEducationalInformation[]
  );

  const onEducationDetailsFieldChange = (changedValues: unknown, key: string, index: number) => {
    const updatedEducationDetails = educationDetails.map((educationDetail, i) => {
      if (i === index) {
        if (key === 'issuingInstitution') {
          setShowOtherSection((prev) => {
            const updated = [...prev];
            updated[index] = changedValues === 'Other';
            return updated;
          });
        }
        return {
          ...educationDetail,
          [key]: changedValues
        };
      }
      return educationDetail;
    });
    setEducationDetails(updatedEducationDetails);
  };
  const normalizeGraduationYear = (value: number | Dayjs) => {
    if (typeof value === 'number') {
      return { value: dayjs().year(value).utc() };
    }
    if (typeof value !== 'string') { // dayjs type
      return {value};
    }
    return { value: null };
  }


  const ProfessionalDetailsForm = (
    <Form
      form={form}
      initialValues={initialValues}
      layout="vertical"
      onFinish={async (values) => {
        const professionalInfo: ApplicantUserProfessionalInput = {
          citizenshipAtTimeEnteredMedicalInstitution: values.citizenshipAtTimeEnteredMedicalInstitution,
          healthProfession: values.healthProfession
        };
        professionalInfo.primaryEducationalInformation = values.primaryEducationalInformation.map((eduInfo: ApplicantUserPrimaryEducationalInformation) => {
          return {
            attendanceEndedAt: FormatDate(eduInfo.attendanceEndedAt, APOLLO_DATE_FORMAT),
            attendanceStartedAt: FormatDate(eduInfo.attendanceStartedAt, APOLLO_DATE_FORMAT),
            degreeIssuedAt: FormatDate(eduInfo.degreeIssuedAt, APOLLO_DATE_FORMAT),
            degreeTitle: eduInfo.degreeTitle,
            graduationYear: typeof eduInfo.graduationYear === 'number' ? eduInfo.graduationYear : dayjs(eduInfo.graduationYear).year(),
            issuingInstitutionId: eduInfo.issuingInstitution?.id !== 'Other' ? eduInfo.issuingInstitution?.id : null,
            otherIssuingInstitution:
              eduInfo.issuingInstitution?.id === 'Other'
                ? {
                    addressLine1: eduInfo.otherIssuingInstitution?.addressLine1,
                    city: eduInfo.otherIssuingInstitution?.city,
                    country: eduInfo.otherIssuingInstitution?.country,
                    name: eduInfo.otherIssuingInstitution?.name,
                    stateOrProvince: eduInfo.otherIssuingInstitution?.stateOrProvince,
                    zipOrPostalCode: eduInfo.otherIssuingInstitution?.zipOrPostalCode
                  }
                : null,
            id: eduInfo.id
          };
        });
        onSave(professionalInfo);
        setIsSubmitting(true);
      }}
    >
      <HealthProfessionDropdownContainer name={['healthProfession']} label="What is your Health Profession?" style={{ width: 500 }} placeholder="Select Health Profession" />
      <Form.Item
        name={['citizenshipAtTimeEnteredMedicalInstitution']}
        validateTrigger={['onChange', 'onBlur']}
        rules={[
          {
            required: true,
            message: 'Please select Citizenship Upon Entering Educational Institution'
          }
        ]}
        label="Citizenship Upon Entering Educational Institution"
      >
        <Select placeholder="Country" className="w-[200px]" showSearch optionFilterProp="children">
          {countryData.map((item: Country) => (
            <Select.Option key={item.countryCode} value={item.countryCode}>
              {item.countryName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <p className="ant-form-item-label">Primary Educational Institution Information (Minimum 1 - Max 5)</p>
      <Form.List
        name={['primaryEducationalInformation']}
        rules={[
          {
            validator: async (_, educationDetails) => {
              if (!educationDetails || educationDetails.length < 1) {
                return Promise.reject(new Error('Please add at least one primary educational information'));
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <div key={field.key} className="mb-4 flex w-full">
                    <div className="inline-block align-top pr-2">
                      <MinusCircleOutlined
                        className="dynamic-delete-button"
                        onClick={() => {
                          setEducationDetails((prev) => prev.filter((_, i) => i !== index));
                          setShowOtherSection((prev) => prev.filter((_, i) => i !== index));
                          remove(field.name);
                        }}
                      />
                    </div>
                    <div className="flex flex-col items-start pt-[3px] pl-2 border-l border-darkgrey">
                      <Form.Item name={[index, 'id']} hidden={true}>
                        <Input hidden={true} />
                      </Form.Item>
                      <Form.Item
                        name={[index, 'degreeTitle']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter Degree Title'
                          }
                        ]}
                        label="Degree Title"
                      >
                        <Input className="w-[200px]" onChange={(e) => onEducationDetailsFieldChange(e.target.value, 'degreeTitle', index)} placeholder="Degree Title" />
                      </Form.Item>
                      <div className="flex gap-2">
                        <Form.Item
                          name={[index, 'attendanceStartedAt']}
                          valuePropName="value"
                          rules={[
                            {
                              required: true,
                              message: 'Please select Attendance Start Date'
                            }
                          ]}
                          label="Attendance Start Date"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value) : ''
                          })}
                        >
                          <DatePicker
                            className="w-[200px]"
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.attendanceStartedAt
                                ? FormatDate(professionalInformation?.primaryEducationalInformation?.[index]?.attendanceStartedAt)
                                : ''
                            }
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            maxDate={dayjs(educationDetails[index].attendanceEndedAt || dayjs(), DATE_FORMAT)}
                            onChange={(date) => onEducationDetailsFieldChange(FormatDate(date), 'attendanceStartedAt', index)}
                          />
                        </Form.Item>
                        <Form.Item
                          name={[index, 'attendanceEndedAt']}
                          valuePropName="value"
                          rules={[
                            {
                              required: true,
                              message: 'Please select Attendance End Date'
                            }
                          ]}
                          label="Attendance End Date"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value) : ''
 })}
                        >
                          <DatePicker
                            className="w-[200px]"
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            minDate={dayjs(educationDetails[index].attendanceStartedAt)}
                            maxDate={dayjs(dayjs().endOf('day'), DATE_FORMAT)}
                            onChange={(date) => onEducationDetailsFieldChange(FormatDate(date), 'attendanceEndedAt', index)}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.attendanceEndedAt
                                ? FormatDate(professionalInformation?.primaryEducationalInformation?.[index]?.attendanceEndedAt)
                                : ''
                            }
                          />
                        </Form.Item>
                      </div>
                      <div className="flex gap-2">
                        <Form.Item
                          name={[index, 'degreeIssuedAt']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select Date Degree Issued'
                            }
                          ]}
                          label="Date Degree Issued"
                          getValueProps={(value) => ({
                            value: value ? dayjs(value) : ''
                          })}
                        >
                          <DatePicker
                            className="w-[200px]"
                            format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                            minDate={dayjs(educationDetails[index].attendanceEndedAt)}
                            maxDate={dayjs(dayjs(), DATE_FORMAT)}
                            onChange={(date) => onEducationDetailsFieldChange(FormatDate(date), 'degreeIssuedAt', index)}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.degreeIssuedAt
                                ? FormatDate(professionalInformation?.primaryEducationalInformation?.[index]?.degreeIssuedAt)
                                : ''
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name={[index, 'graduationYear']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select Graduation Year'
                            }
                          ]}
                          label="Graduation Year"
                          getValueProps={(value) => {
                            return  normalizeGraduationYear (value)
                          }}
                        >
                          {/* <Input placeholder="Graduation Year" maxLength={10} /> */}
                          <DatePicker
                            className="w-[200px]"
                            picker="year"
                            maxDate={dayjs(dayjs(), 'YYYY')}
                            format={'YYYY'}
                            onChange={(date) => onEducationDetailsFieldChange(FormatDate(date, 'YYYY'), 'graduationYear', index)}
                            value={
                              professionalInformation?.primaryEducationalInformation?.[index]?.graduationYear
                                ? dayjs(professionalInformation?.primaryEducationalInformation?.[index]?.graduationYear).format('YYYY')
                                : ''
                            }
                          />
                        </Form.Item>
                      </div>
                      <Form.Item
                        name={[index, 'issuingInstitution', 'id']}
                        rules={[
                          {
                            required: true,
                            message: 'Please select Issuing Institution'
                          }
                        ]}
                        label="Issuing Institution"
                      >
                        <Select
                          className="w-[410px]"
                          placeholder="Issuing Institution"
                          onSelect={(issuingInstitution) => {
                            onEducationDetailsFieldChange(issuingInstitution, 'issuingInstitution', index);
                            handleEntityChange(issuingInstitution, index);
                          }}
                          defaultValue={showOtherSection[index] ? 'Other' : educationDetails[index]?.issuingInstitution?.entityName}
                        >
                          {entities.map((entity) => (
                            <Select.Option key={entity.id} value={entity.id}>
                              {entity.entityName}
                            </Select.Option>
                          ))}
                          <Select.Option key="Other" value="Other">
                            Other
                          </Select.Option>
                        </Select>
                      </Form.Item>
                      {showOtherSection[index] && (
                        <>
                          <Form.Item
                            name={[index, 'otherIssuingInstitution', 'name']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter Institution Name'
                              }
                            ]}
                            label="Institution Name"
                          >
                            <Input
                              className="w-[410px]"
                              onChange={(otherIssuingInstitutionName) => onEducationDetailsFieldChange(otherIssuingInstitutionName, 'name', index)}
                              placeholder="Institution Name"
                            />
                          </Form.Item>
                          <Form.Item
                            name={[index, 'otherIssuingInstitution', 'addressLine1']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter Address'
                              }
                            ]}
                            label="Address"
                          >
                            <Input
                              className="w-[410px]"
                              onChange={(otherIssuingInstitutionAddress) => onEducationDetailsFieldChange(otherIssuingInstitutionAddress, 'addressLine1', index)}
                              placeholder="Address"
                            />
                          </Form.Item>
                          <Form.Item
                            name={[index, 'otherIssuingInstitution', 'country']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Please select Country'
                              }
                            ]}
                            label="Country"
                          >
                            <Select
                              showSearch
                              optionFilterProp="children"
                              className="w-[410px]"
                              placeholder="Country"
                              onChange={(otherIssuingInstitutionCountry) => {
                                onEducationDetailsFieldChange(otherIssuingInstitutionCountry, 'country', index);
                                handleCountryChange(otherIssuingInstitutionCountry, index);
                              }}
                            >
                              {countryData.map((item: Country) => (
                                <Select.Option key={item.countryCode} value={item.countryCode}>
                                  {item.countryName}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <div className="flex gap-2 w-full">
                            <Form.Item
                              className="w-[150px]"
                              label="City"
                              name={[index, 'otherIssuingInstitution', 'city']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter City'
                                }
                              ]}
                              validateTrigger={['onChange', 'onBlur']}
                            >
                              <Input
                                onChange={(otherIssuingInstitutionCity) => onEducationDetailsFieldChange(otherIssuingInstitutionCity, 'city', index)}
                                placeholder="City"
                              />
                            </Form.Item>
                            <StateSelect
                              name={[index, 'otherIssuingInstitution', 'stateOrProvince']}
                              selectedCountry={selectedCountries[index]} // Pass the corresponding country
                              countryData={countryData}
                            />
                            <Form.Item
                              label="Zip/Postal Code"
                              name={[index, 'otherIssuingInstitution', 'zipOrPostalCode']}
                              rules={zipCodeRules[index]}
                              validateTrigger={['onChange', 'onBlur']}
                            >
                              <Input
                                className="w-[100px]"
                                maxLength={10}
                                placeholder="Zip/Postal Code"
                                onChange={(otherIssuingInstitutionZip) => onEducationDetailsFieldChange(otherIssuingInstitutionZip, 'zipOrPostalCode', index)}
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {fields.length < 5 ? (
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      setEducationDetails([
                        ...educationDetails,
                        {
                          id: '',
                          degreeTitle: '',
                          attendanceStartedAt: '',
                          attendanceEndedAt: '',
                          degreeIssuedAt: '',
                          issuingInstitution: { id: '', entityName: '' },
                          otherIssuingInstitution: {
                            name: '',
                            addressLine1: '',
                            city: '',
                            stateOrProvince: '',
                            zipOrPostalCode: '',
                            country: ''
                          }
                        }
                      ]);
                      setShowOtherSection((prev) => [...prev, false]);
                      add();
                    }}
                    block
                    className="w-[500px] pr-2"
                  >
                    Other Educational Institution
                  </Button>
                </Form.Item>
              ) : (
                <></>
              )}
            </div>
          );
        }}
      </Form.List>
      {formError && educationDetails.length < 1 && <p className="text-[#ff4d4f]">{formError}</p>}
      <div className="flex items-center justify-center gap-4 w-full">
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button htmlType="submit" className="bg-[#3F4373] text-[#D9D9D9]" value={'save'} loading={isSubmitting}>
          Save
        </Button>
      </div>
    </Form>
  );

  return <EditProfile title={EDIT_TITLE} form={ProfessionalDetailsForm} />;
};
